import React, { useState, useEffect } from 'react';
import TextHelper from 'src/util/Text';
import TextInput from '../controls/forms/TextInput';
import Checkbox from '../controls/forms/Checkbox';
import useDebounce from 'src/util/hooks/use-debounce';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Dialog from '../common/Dialog.redesign';
import Horizon from 'src/models/tract_detail/Horizon';
import { useFetch } from 'use-http';

interface ICloneDataDialogProps {
    context: 'ownership' | 'surfaceOwnership' | 'workingInterest';
    contextParams: { [key: string]: number | string };
    cloneData: (context: string, options: string) => void;
    onCloseDialog?: () => void;
}

type CloneCheckOptions = {
    removeOwnership: boolean;
    ownership: boolean;
    agreementhold: boolean;
    workingInterest: boolean;
    overridingRoyaltyInterest: boolean;
};

interface IOwnershipPanelProps {
    isInCreateHorizonDialog?: boolean;
    isFromChild?: boolean;
    horizons?: Array<Horizon>;
    onNext?: (
        cloneId: string,
        copyData: boolean,
        copyOwnership: boolean,
        copyLeasehold: boolean,
        copyWorkingInterest: boolean,
        copyOverridingRoyaltyInterest: boolean,
        isChildTract: boolean
    ) => void;
    onPrev?: () => void;
    onCancel?: () => void;
    contextParams: { [key: string]: number | string };
    cloneData?: (context: string, options: string) => void;
    onCloseDialog?: () => void;
}

export const OwnershipPanel: React.FC<IOwnershipPanelProps> = (props) => {
    const defaultOptions: CloneCheckOptions = {
        removeOwnership: false,
        ownership: true,
        agreementhold: true,
        workingInterest: true,
        overridingRoyaltyInterest: true,
    };

    const { get, response } = useFetch();
    const [options, setOptions] = useState(defaultOptions);
    const [currentHorizonNumber, setCurrentHorizonNumber] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('');
    const [verificationResult, setVerificationResult] = useState(false);
    const debouncedHorizonNumber = useDebounce(currentHorizonNumber, 500);

    useEffect(() => {
        const horizonNumberRegex = /^[0-9]+\.[0-9]+-[0-9]+$/;
        const horizonChildRegex = /^[0-9]+$/;

        if (props.isFromChild === true && horizonChildRegex.test(currentHorizonNumber)) {
            setVerificationStatus('Verifying Horizon Number');
            verifyChildId(parseInt(currentHorizonNumber, 10));
        } else if (!horizonNumberRegex.test(currentHorizonNumber)) {
            setVerificationStatus('Invalid Horizon Number Provided');
        } else {
            setVerificationStatus('Verifying Horizon Number');
            verifyId('horizon', `${props.contextParams.clientNumber}.${props.contextParams.projectNumber}.${currentHorizonNumber}`);
            setVerificationResult(false);
        }
    }, [debouncedHorizonNumber]);

    const verifyId = async (context: string, id: string) => {
        console.log(context, id);
        const params = `?context=${context}&id=${id}`;
        const horizonVerified = await get(`v1.0/search/verify-id${params}`);

        if (response.ok) {
            console.log('Verify Id Result:', horizonVerified);
            if (horizonVerified.result === true) {
                setVerificationStatus('Horizon found');
            } else {
                setVerificationStatus('Horizon not found');
            }
            setVerificationResult(horizonVerified.result);
        }
    };

    const verifyChildId = (searchHorizonNumber: number) => {
        if (props.horizons.indexOf(props.horizons.find((h) => h.horizonInterval.horizonNumber === searchHorizonNumber)) > -1) {
            setVerificationStatus('Horizon found');
            setVerificationResult(true);
        } else {
            setVerificationStatus('Horizon not found');
            setVerificationResult(false);
        }
    };

    const updateChecks = (event: React.ChangeEvent<HTMLInputElement>, level: number) => {
        const value = event.currentTarget.checked;
        switch (level) {
            case 0: {
                if (props.isInCreateHorizonDialog) {
                    if (!value) {
                        setOptions({ ...options, ownership: value, agreementhold: false, workingInterest: false, overridingRoyaltyInterest: false });
                    } else {
                        setOptions({ ...options, ownership: value });
                    }
                // } else {
                //     setOptions({ ...options, removeOwnership: value });
                }
                break;
            }
            case 1: {
                if (!value) {
                    setOptions({ ...options, agreementhold: value, workingInterest: false, overridingRoyaltyInterest: false });
                } else {
                    setOptions({ ...options, agreementhold: value });
                }
                break;
            }
            case 2: {
                if (!value) {
                    setOptions({ ...options, workingInterest: value, overridingRoyaltyInterest: false });
                } else {
                    setOptions({ ...options, workingInterest: value, agreementhold: true });
                }
                break;
            }
            case 3: {
                if (value) {
                    setOptions({ ...options, agreementhold: true, workingInterest: true, overridingRoyaltyInterest: value });
                } else {
                    setOptions({ ...options, overridingRoyaltyInterest: value });
                }
                break;
            }
            default:
                return;
        }
    };

    const handleCloneData = (copyData: boolean) => {
        let cloneHorizon: string;
        if (props.isFromChild === true) {
            cloneHorizon = `${props.horizons.find((h) => h.horizonInterval.horizonNumber === parseInt(debouncedHorizonNumber, 10))?.id}`;
        } else {
            cloneHorizon = `${props.contextParams.clientNumber}.${props.contextParams.projectNumber}.${debouncedHorizonNumber}`;
        }
        if (props.isInCreateHorizonDialog) {
            props.onNext(
                cloneHorizon,
                copyData,
                options.ownership,
                options.agreementhold,
                options.workingInterest,
                options.overridingRoyaltyInterest,
                props.isFromChild
            );
        } else {
            if (!verificationResult) {
                return;
            }

            const cloneOptions = {
                cloneHorizon,
                // removeOwnership: options.removeOwnership,
                leasehold: options.agreementhold,
                workingInterest: options.workingInterest,
                overridingRoyaltyInterest: options.overridingRoyaltyInterest,
            };

            props.cloneData('ownership', JSON.stringify(cloneOptions));
            props.onCloseDialog();
        }
    };

    const showStatusMessage = () => {
        if (debouncedHorizonNumber) {
            if (verificationStatus.indexOf('Verifying') >= 0) {
                return (
                    <span key="loading-panel" style={{ marginRight: '2rem', paddingTop: '0.4rem' }}>
                        <span style={{ marginRight: '1rem' }}>
                            <i className="fas fa-spinner fa-pulse" style={{ color: 'green' }} />
                        </span>
                        {verificationStatus}
                    </span>
                );
            } else {
                return (
                    <span key="error-panel" style={{ marginRight: '2rem', paddingTop: '0.4rem' }}>
                        {verificationStatus}
                    </span>
                );
            }
        }

        return undefined;
    };

    const showActionPanel = () => {
        if (props.isInCreateHorizonDialog) {
            const buttonStyle = {
                marginRight: '1rem',
            };

            return (
                <>
                    {showStatusMessage()}
                    <Button intent="primary" size="small" text="Prev" style={buttonStyle} onClick={props.onPrev} />
                    <Button
                        intent="primary"
                        size="small"
                        text="Clone Horizon"
                        style={buttonStyle}
                        onClick={() => handleCloneData(true)}
                        disabled={!verificationResult}
                    />
                    <Button intent="primary" size="small" text="Create Blank Horizon" style={buttonStyle} onClick={() => handleCloneData(false)} />
                    <Button intent="primary" size="small" text="Cancel" onClick={props.onCancel} />
                </>
            );
        } else {
            return (
                <>
                    {showStatusMessage()}
                    <Button
                        intent="primary"
                        size="small"
                        disabled={!verificationResult}
                        text="Copy Horizon Ownership"
                        onClick={() => handleCloneData(true)}
                    />
                </>
            );
        }
    };

    let style: React.CSSProperties = {};
    if (props.isInCreateHorizonDialog) {
        style = {
            width: '60rem',
        };
    }
    return (
        <div style={style}>
            <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>
                Enter Horizon {props.isFromChild ? 'Number' : 'Id (Section.Tract-Horizon)'}:
            </div>
            <TextInput label="Horizon Number" onChange={(e) => setCurrentHorizonNumber(e.target.value)} />
            <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Copy Options:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {/* // REMOVING THIS OPTION, AS THERE ARE TOO MANY COMPLICATIONS TO MASS DELETING OWNERSHIPS. */}
                {props.isInCreateHorizonDialog && (
                    <div style={{ width: '50%' }}>
                        <Checkbox label="Ownership" value={options.ownership} onChange={(e) => updateChecks(e, 0)} disabled={!verificationResult} />
                    </div>
                )}
                {/* <div style={{ width: '50%' }}>
                    {!props.isInCreateHorizonDialog && (
                        <Checkbox
                            label="Replace Current Ownership"
                            value={options.removeOwnership}
                            onChange={(e) => updateChecks(e, 0)}
                            disabled={!verificationResult}
                        />
                    )}
                    {props.isInCreateHorizonDialog && (
                        <Checkbox label="Ownership" value={options.ownership} onChange={(e) => updateChecks(e, 0)} disabled={!verificationResult} />
                    )}
                </div> */}
                <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Leasehold"
                        value={options.agreementhold}
                        onChange={(e) => updateChecks(e, 1)}
                        disabled={props.isInCreateHorizonDialog ? !verificationResult || !options.ownership : !verificationResult}
                    />
                </div>
                <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Working Interest"
                        value={options.workingInterest}
                        onChange={(e) => updateChecks(e, 2)}
                        disabled={!verificationResult || !options.agreementhold}
                    />
                </div>
                <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Overriding Royalty Interest"
                        value={options.overridingRoyaltyInterest}
                        onChange={(e) => updateChecks(e, 3)}
                        disabled={!verificationResult || !options.workingInterest}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%',
                        paddingTop: '1rem',
                        borderTop: '1px solid #ccc',
                        marginTop: '.5rem',
                    }}
                >
                    {showActionPanel()}
                </div>
            </div>
        </div>
    );
};

OwnershipPanel.defaultProps = {
    isInCreateHorizonDialog: false,
};

export const SurfaceOwnershipPanel: React.FC<IOwnershipPanelProps> = (props) => {
    const defaultOptions: CloneCheckOptions = {
        removeOwnership: false,
        ownership: true,
        agreementhold: true,
        workingInterest: true,
        overridingRoyaltyInterest: true,
    };

    const { get, response } = useFetch();
    const [options, setOptions] = useState(defaultOptions);
    const [currentSurfaceTractNumber, setCurrentSurfaceTractNumber] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('');
    const [verificationResult, setVerificationResult] = useState(false);
    const debouncedSurfaceTractNumber = useDebounce(currentSurfaceTractNumber, 500);

    useEffect(() => {
        const surfaceTractNumberRegex = /^[0-9]+\.[0-9]+$/;

        if (!surfaceTractNumberRegex.test(currentSurfaceTractNumber)) {
            setVerificationStatus('Invalid Surface Tract Number Provided');
        } else {
            setVerificationStatus('Verifying Surface Tract Number');
            verifyId('surface tract', `${props.contextParams.clientNumber}.${props.contextParams.projectNumber}.${currentSurfaceTractNumber}`);
            setVerificationResult(false);
        }
    }, [debouncedSurfaceTractNumber]);

    const verifyId = async (context: string, id: string) => {
        console.log(context, id);
        const params = `?context=${context}&id=${id}`;
        const surfaceTractVerified = await get(`v1.0/search/verify-id${params}`);

        if (response.ok) {
            console.log('Verify Id Result:', surfaceTractVerified);
            if (surfaceTractVerified.result === true) {
                setVerificationStatus('Surface Tract found');
            } else {
                setVerificationStatus('Surface Tract not found');
            }
            setVerificationResult(surfaceTractVerified.result);
        }
    };

    const updateChecks = (event: React.ChangeEvent<HTMLInputElement>, level: number) => {
        const value = event.currentTarget.checked;
        switch (level) {
            // case 0: {
            //     setOptions({ ...options, removeOwnership: value });
            //     break;
            // }
            case 1: {
                if (!value) {
                    setOptions({ ...options, agreementhold: value, workingInterest: false, overridingRoyaltyInterest: false });
                } else {
                    setOptions({ ...options, agreementhold: value });
                }
                break;
            }
            case 2: {
                if (!value) {
                    setOptions({ ...options, workingInterest: value, overridingRoyaltyInterest: false });
                } else {
                    setOptions({ ...options, workingInterest: value, agreementhold: true });
                }
                break;
            }
            default:
                return;
        }
    };

    const handleCloneData = () => {
        const cloneSurfaceTract = `${props.contextParams.clientNumber}.${props.contextParams.projectNumber}.${debouncedSurfaceTractNumber}`;
        if (!verificationResult) {
            return;
        }

        const cloneOptions = {
            cloneSurfaceTract: cloneSurfaceTract,
            // removeOwnership: options.removeOwnership,
            agreementhold: options.agreementhold,
            workingInterest: options.workingInterest,
            overridingRoyaltyInterest: options.overridingRoyaltyInterest,
        };

        props.cloneData('surfaceOwnership', JSON.stringify(cloneOptions));
        props.onCloseDialog();
    };

    const showStatusMessage = () => {
        if (debouncedSurfaceTractNumber) {
            if (verificationStatus.indexOf('Verifying') >= 0) {
                return (
                    <span key="loading-panel" style={{ marginRight: '2rem', paddingTop: '0.4rem' }}>
                        <span style={{ marginRight: '1rem' }}>
                            <i className="fas fa-spinner fa-pulse" style={{ color: 'green' }} />
                        </span>
                        {verificationStatus}
                    </span>
                );
            } else {
                return (
                    <span key="error-panel" style={{ marginRight: '2rem', paddingTop: '0.4rem' }}>
                        {verificationStatus}
                    </span>
                );
            }
        }

        return undefined;
    };

    const showActionPanel = () => {
        return (
            <>
                {showStatusMessage()}
                <Button
                    intent="primary"
                    size="small"
                    disabled={!verificationResult}
                    text="Copy Surface Ownership"
                    onClick={() => handleCloneData()}
                />
            </>
        );
    };

    let style: React.CSSProperties = {};
    if (props.isInCreateHorizonDialog) {
        style = {
            width: '60rem',
        };
    }
    return (
        <div style={style}>
            <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Enter Surface Tract Number:</div>
            <TextInput label="Surface Tract Number" onChange={(e) => setCurrentSurfaceTractNumber(e.target.value)} />
            <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Copy Options:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {/* REMOVING THIS OPTION, AS THERE ARE TOO MANY COMPLICATIONS TO MASS DELETING OWNERSHIPS. */}
                {/* <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Replace Current Ownership"
                        value={options.removeOwnership}
                        onChange={(e) => updateChecks(e, 0)}
                        disabled={!verificationResult}
                    />
                </div> */}
                <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Leasehold"
                        value={options.agreementhold}
                        onChange={(e) => updateChecks(e, 1)}
                        disabled={!verificationResult || !options.ownership}
                    />
                </div>
                <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Working Interest"
                        value={options.workingInterest}
                        onChange={(e) => updateChecks(e, 2)}
                        disabled={!verificationResult || !options.agreementhold}
                    />
                </div>
                <div style={{ width: '50%' }}>
                    <Checkbox
                        label="Overriding Royalty Interest"
                        value={options.overridingRoyaltyInterest}
                        onChange={(e) => updateChecks(e, 3)}
                        disabled={!verificationResult || !options.workingInterest}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%',
                        paddingTop: '1rem',
                        borderTop: '1px solid #ccc',
                        marginTop: '.5rem',
                    }}
                >
                    {showActionPanel()}
                </div>
            </div>
        </div>
    );
};

SurfaceOwnershipPanel.defaultProps = {
    isInCreateHorizonDialog: false,
};

const CloneDataDialog = (props: ICloneDataDialogProps) => {
    const returnPanel = () => {
        switch (props.context) {
            case 'ownership':
                return <OwnershipPanel {...props} />;
            case 'surfaceOwnership':
                return <SurfaceOwnershipPanel {...props} />;
            default:
                return <div>I'm workin' heyah!</div>;
        }
    };

    return <Dialog title={`Copy ${TextHelper.toProperCase(props.context)}`}>{returnPanel()}</Dialog>;
};

export default CloneDataDialog;



// WEBPACK FOOTER //
// ./src/components/dialogs/CloneDataDialog.tsx